import React, { useEffect, useState } from 'react';
import {
  ClearerType,
  InvestigationDocuments,
  RequestedInvestigationDocument,
  ResponseCodes,
} from '@tradingblock/types';
import { Button, FormGroup, Text } from '@tradingblock/components';
import { FileError, useDropzone } from 'react-dropzone';
import { useApi } from '../../context';
import { useCallback } from 'react';
import { ApiResponse } from '@tradingblock/api';
import { useDispatcher } from '../hooks';
import { ApplicationActions } from '../../state/actions';
import { PDFDocument } from 'pdf-lib';

/**
 * InvestigationUpload component properties
 *
 * @property {RequestedInvestigationDocument} requestedInvestigationDocument Requested document infomation.
 * @property {string} requestedInvestigationDocument Type of requsted document. Application is internal, investigation is apex related documents.
 * @property {number} accountId The account ID for the upload.
 * @property {number} [accountHolderId] The account holder id, needed only for application uploads.
 * @property {number} [investigationId] The investigation id, needed only for investigation uploads.
 * @property {boolean} [disabled] Disables buttons.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} [setDisabled] Sets disable boolean.
 */
export interface InvestigationUploadProps {
  requestedInvestigationDocument: RequestedInvestigationDocument;
  type: 'application' | 'investigation';
  accountId: number;
  accountHolderId?: number;
  investigationId?: number;
  disabled: boolean;
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  clearer?: ClearerType | null;
}

/**
 * An independent upload widget for uploading investigations and application specific documents
 *
 * @param {InvestigationUploadProps} props
 * @returns {JSX.Element}
 */
export default function InvestigationUpload({
  requestedInvestigationDocument,
  type,
  accountId,
  accountHolderId,
  investigationId,
  disabled,
  setDisabled,
  clearer,
}: InvestigationUploadProps) {
  // Checks that accountHolderId is set for application uploads
  // and investigationId for investigation uploads
  if (type === 'application' && accountHolderId === undefined) {
    throw new Error('accountHolderId is required for an application upload');
  } else if (type === 'investigation' && investigationId === undefined) {
    throw new Error('investigationId is required for an investigation upload');
  }
  if (requestedInvestigationDocument.requestedDocuments.length <= 0) {
    throw new Error('Need one requested document to process');
  }

  // Used hooks
  const api = useApi();
  const dispatch = useDispatcher();

  // Used states
  const [uploadingDocument, setUploadingDocument] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<FileError[]>([]);
  const [selectedDocumentName, setSelectedDocumentName] = useState<InvestigationDocuments>(
    requestedInvestigationDocument.requestedDocuments[0]
  );
  const [needToSelectDocumentName, setNeedToSelectDocumentName] = useState<boolean>(
    requestedInvestigationDocument.requestedDocuments.length > 1
  );
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [frontImgPreview, setFrontImgPreview] = useState<string | null>(null);
  const [backImgPreview, setBackImgPreview] = useState<string | null>(null);

  useEffect(() => {
    setDisabled(uploadingDocument);
  }, [uploadingDocument]);

  const fetchStatus = (accountId: number) => {
    dispatch(ApplicationActions.requestApplicationStatus({ accountId }));
  };

  const timeoutMs = 10000;
  function withTimeout<T>(promise: Promise<T>): Promise<T> {
    const timeoutPromise = new Promise<T>((_, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Operation timed out'));
      }, timeoutMs);

      promise.finally(() => clearTimeout(timer)); // Clear the timer when the promise settles
    });

    return Promise.race([promise, timeoutPromise]);
  }

  const renderImageToCanvas = async (imageSrc: any) => {
    const img = new Image();
    img.src = imageSrc;
    await new Promise(resolve => (img.onload = resolve));
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0);
    }
    return canvas;
  };

  async function createMergedPDF(files: File[]): Promise<File | undefined> {
    const pdfDoc = await PDFDocument.create();

    for (const file of files) {
      if (!file) continue;

      if (file.type === 'application/pdf') {
        // Load and copy pages from the existing PDF
        const existingPdfBytes = await file.arrayBuffer();
        const existingPdf = await PDFDocument.load(existingPdfBytes);
        const pages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
        pages.forEach(page => pdfDoc.addPage(page));
      } else if (file.type.match('image.*')) {
        // Load and embed the image
        let imageData = await file.arrayBuffer();
        let image;

        if (file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png') {
          image = file.type.includes('png') ? await pdfDoc.embedPng(imageData) : await pdfDoc.embedJpg(imageData);
        } else if (file.type === 'image/gif') {
          // Convert GIF to JPEG before embedding
          imageData = await convertGifToJpeg(file);
          image = await pdfDoc.embedJpg(imageData);
        }

        if (image) {
          const page = pdfDoc.addPage();
          const scale = Math.min(page.getWidth() / image.width, page.getHeight() / image.height);
          page.drawImage(image, {
            x: 0,
            y: page.getHeight() - image.height * scale,
            width: image.width * scale,
            height: image.height * scale,
          });
        } else {
          console.error('Failed to convert file');
          continue; // Skip failed file
        }
      } else {
        console.warn('Unsupported file type.');
        continue; // Skip unsupported files
      }
    }

    const pdfBytes = await pdfDoc.save();
    return new File([pdfBytes], 'combined.pdf', { type: 'application/pdf' });
  }

  async function convertGifToJpeg(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, img.width, img.height);
          canvas.toBlob(blob => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve(reader.result as ArrayBuffer);
              };
              reader.onerror = reject;
              reader.readAsArrayBuffer(blob);
            } else {
              reject(new Error('Could not convert GIF to JPEG'));
            }
          }, 'image/jpeg');
        } else {
          reject(new Error('Could not convert GIF to JPEG'));
        }
      };
      img.onerror = reject;
      const reader = new FileReader();
      reader.onload = e => (img.src = e.target.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const handleCombinedUpload = async () => {
    if (frontImage && backImage) {
      setUploadingDocument(true);
      try {
        let fileToUpload = await createMergedPDF([frontImage, backImage]);

        if (fileToUpload) {
          let response;
          if (type === 'application' && accountHolderId !== undefined) {
            response = await withTimeout(
              api.application
                .uploadApplicationDocument(fileToUpload, accountId, accountHolderId, selectedDocumentName)
                .then(response => {
                  processUploadResponse(response);
                })
            );
          } else if (type === 'investigation' && investigationId !== undefined) {
            response = await withTimeout(
              api.application
                .uploadInvestigationDocument(fileToUpload, accountId, investigationId, selectedDocumentName)
                .then(response => {
                  processUploadResponse(response);
                })
            );
          }
        } else {
          throw new Error('File to upload is undefined.');
        }
      } catch (error) {
        setErrors([{ message: 'An error occurred while processing the upload.', code: 'UPLOAD_ERROR' }]);
        setUploadingDocument(false);
      }
    }
  };

  //Processes the upload response from the api
  const processUploadResponse = useCallback(
    (response: ApiResponse<number>) => {
      // check if the upload was not successful and set the errors/initial states.
      if (response.responseCode !== 0) {
        setUploadingDocument(false);
        setSelectedDocumentName(requestedInvestigationDocument.requestedDocuments[0]);
        setNeedToSelectDocumentName(requestedInvestigationDocument.requestedDocuments.length > 0);
        setErrors([
          {
            message: ResponseCodes[response.responseCode].description as string,
            code: ResponseCodes[response.responseCode].name,
          },
        ]);
      } else {
        // There were no errors, reset the states and set success to true
        setUploadSuccess(true);
        setErrors([]);
        const fetchStatusTimer = setTimeout(fetchStatus, 1500, accountId);
        return () => clearTimeout(fetchStatusTimer);
      }
    },
    [requestedInvestigationDocument]
  );

  // maxFileSize variable set to 12MB
  const maxFileSize = 12 * 1024 * 1024;

  const isIdCard =
    selectedDocumentName === InvestigationDocuments.StateIdCard ||
    selectedDocumentName === InvestigationDocuments.DriversLicense;

  const handleImageDrop = (side: 'front' | 'back') => async (acceptedFiles: File[]) => {
    // if the side is front, set the front image state
    // if the side is back, set the back image state
    const file = acceptedFiles[0];
    // if both the front and back images are set, combine them into a single PDF and upload automatically without user intervention
    if (side === 'front') {
      setFrontImgPreview(URL.createObjectURL(file));
      setFrontImage(file);
    } else if (side === 'back') {
      setBackImgPreview(URL.createObjectURL(file));
      setBackImage(file);
    }
  };

  // Dropzone properties for the upload input field
  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    onDrop: () => {
      setUploadingDocument(true);
    },
    onDropAccepted: acceptedFiles => {
      // If the files where accepted from frontend validation
      // The base on the type, upload the document to the backend
      if (type === 'application' && accountHolderId !== undefined) {
        withTimeout(
          api.application
            .uploadApplicationDocument(acceptedFiles[0], accountId, accountHolderId, selectedDocumentName)
            .then(response => {
              processUploadResponse(response);
            })
        ).catch(error => {
          setErrors([{ message: 'An error occurred while processing the upload.', code: 'UPLOAD_ERROR' }]);
          setUploadingDocument(false);
        });
      } else if (type === 'investigation' && investigationId !== undefined) {
        withTimeout(
          api.application
            .uploadInvestigationDocument(acceptedFiles[0], accountId, investigationId, selectedDocumentName)
            .then(response => {
              processUploadResponse(response);
            })
        ).catch(error => {
          setErrors([{ message: 'An error occurred while processing the upload.', code: 'UPLOAD_ERROR' }]);
          setUploadingDocument(false);
        });
      }
    },
    onDropRejected: fileRejections => {
      //Set the upload failed and the file errors to display
      setUploadingDocument(false);
      setErrors(fileRejections[0].errors);
    },
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, application/pdf, capture=camera',
  });

  const { getRootProps: getFrontRootProps, getInputProps: getFrontInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('front')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, application/pdf, capture=camera',
  });

  const { getRootProps: getBackRootProps, getInputProps: getBackInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('back')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, application/pdf, capture=camera',
  });

  const formatDocumentName = (documentName: string) => {
    const specialCases: { [key: string]: string } = {
      DriversLicense: "Driver's License",
      StateIdCard: 'State ID Card',
      Passport: 'Passport',
      MilitaryId: 'Military ID',
      SsnCard: 'SSN Card',
      SsaLetter: 'SSA Letter',
      IrsItinLetter: 'IRS ITIN Letter',
      OtherGovernmentId: 'Other Government ID',
      CddDocument: 'CDD Document',
      AllPassingCipResults: 'All Passing CIP Results',
      IrsW9Form: 'IRS W-9 Form',
      SignatureImage: 'Signature Image',
      IdDocument: 'ID Document',
      AffiliateApproval: 'Affiliate Approval',
      Document: 'Document',
      Other: 'Other',
      GovernmentId: 'Government ID',
    };

    if (specialCases[documentName]) {
      return specialCases[documentName];
    }

    return (
      documentName
        // Add spaces before each capital letter except the first one
        .replace(/([A-Z])/g, ' $1')
        // Trim any leading spaces
        .trim()
        // Convert to title case
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
    );
  };

  // we need to update the driver's license and state id card document uploads to require a front and back image
  // for RQD cleared accounts we can simply provide a new button to upload the back image separately
  // however for Apex accounts, they will not accept multiple uploads for the same document
  // as such, we need to combine the front and back images into a single PDF for upload

  useEffect(() => {
    if (frontImage && backImage) {
      handleCombinedUpload();
    }
  }, [frontImage, backImage]);

  // if a user has selected a document that requires a front and back image, and they have not uploaded both images, but they change the selected document, we should clear the images
  useEffect(() => {
    if (isIdCard && (frontImage || backImage)) {
      setFrontImage(null);
      setBackImage(null);
      setFrontImgPreview(null);
      setBackImgPreview(null);
    }
  }, [selectedDocumentName]);

  return (
    <FormGroup
      title={
        <Text
          id={'investigationUpload.title'}
          type={'field'}
          data={{
            documentName: (
              <Text id={`investigationUpload.documents.${requestedInvestigationDocument.name}.title`} type={'field'} />
            ),
          }}
        />
      }
    >
      <p>
        <span className="mute">
          <Text
            id={`investigationUpload.documents.${requestedInvestigationDocument.name}`}
            textKey={'description'}
            type={'field'}
          />
        </span>
      </p>
      {!uploadSuccess ? (
        <>
          {needToSelectDocumentName && (
            <>
              <strong>
                <Text id={'investigationUpload.selectDocumentEyebrow'} type={'field'} />
              </strong>
              <ul className={'checklist'}>
                {/** TODO: This should be in a checklist/radio list component, however, current one is too tied to formik
                 * We will refactor when components are in audit phase.
                 */}
                {requestedInvestigationDocument.requestedDocuments.map(requestedDocumentOption => (
                  <li key={`${requestedDocumentOption}_${investigationId ? investigationId : accountHolderId}`}>
                    <label>
                      <span className={'checkcontainer'}>
                        <input
                          type={'checkbox'}
                          name={`${requestedDocumentOption}_${investigationId ? investigationId : accountHolderId}`}
                          id={`${requestedDocumentOption}_${investigationId ? investigationId : accountHolderId}`}
                          value={requestedDocumentOption}
                          checked={requestedDocumentOption === selectedDocumentName}
                          onChange={() => setSelectedDocumentName(requestedDocumentOption)}
                        />
                        <span className={'radiobtn'}></span>
                      </span>
                      <Text id={`investigationUpload.documentOptions.${requestedDocumentOption}`} type={'field'} />
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}

          {!uploadingDocument ? (
            <>
              {!isIdCard && (
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <Button className={'btn-btn btn-primary'} disabled={disabled}>
                    {needToSelectDocumentName ? <Text id={'investigationUpload.selectUpload'} type={'field'} /> : ''}
                    <Text
                      id={'investigationUpload.upload'}
                      type={'field'}
                      data={{
                        documentName: formatDocumentName(selectedDocumentName),
                      }}
                    />
                  </Button>
                </div>
              )}

              {isIdCard && (
                <>
                  {/* Only display if there is no frontImage state currently set*/}
                  {frontImage === null && (
                    <div {...getFrontRootProps()}>
                      <input {...getFrontInputProps()} />
                      <Button className={'btn-btn btn-primary mt-2'} disabled={disabled}>
                        {needToSelectDocumentName ? (
                          <Text id={'investigationUpload.selectUpload'} type={'field'} />
                        ) : (
                          ''
                        )}
                        <Text
                          id={'investigationUpload.upload'}
                          type={'field'}
                          data={{
                            documentName: formatDocumentName(selectedDocumentName) + ' - Front',
                          }}
                        />
                      </Button>
                    </div>
                  )}
                  {/* Only display if there is no backImage state currently set*/}
                  {backImage === null && (
                    <div {...getBackRootProps()}>
                      <input {...getBackInputProps()} />
                      <Button className={'btn-btn btn-primary mt-2'} disabled={disabled}>
                        {needToSelectDocumentName ? (
                          <Text id={'investigationUpload.selectUpload'} type={'field'} />
                        ) : (
                          ''
                        )}
                        <Text
                          id={'investigationUpload.upload'}
                          type={'field'}
                          data={{
                            documentName: formatDocumentName(selectedDocumentName) + ' - Back',
                          }}
                        />
                      </Button>
                    </div>
                  )}
                </>
              )}
              <br />
              <span className="mute" style={{ marginTop: '1rem' }}>
                <Text id={'review-documents.files'} page="account" />
              </span>
            </>
          ) : (
            <span>
              <i className="fas fa-circle-notch fa-spin valid" />{' '}
              <Text id={'investigationUpload.uploading'} type={'field'} />
            </span>
          )}
        </>
      ) : (
        <span>
          <i className="fas fa-check-circle text-success" />{' '}
          <Text id={'investigationUpload.uploadSuccessful'} type={'field'} />
        </span>
      )}
      {errors.length > 0 && (
        <div className="errors">
          <ul>
            {errors.map((error, errorIndex) => (
              // if the error code is INVALID_DOCUMENT, apply some CSS to make the error more visible
              <li
                className="error"
                key={`error_${errorIndex}`}
                style={error.code === 'INVALID_DOCUMENT' ? { fontWeight: 'bold' } : {}}
              >
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </FormGroup>
  );
}
